import {
	IconEyeSlashOutline18 as HiddenIcon,
	IconEyeOutline18 as ShownIcon,
} from "nucleo-ui-outline-18"

import type { PortalInstanceOverrideArgs } from "@productlane/zero/types"

// Radix rejects an empty Select.Item value, so "inherit the workspace team" needs a sentinel.
export const WORKSPACE_DEFAULT_TEAM = "workspace-default"

export type PortalSwitcherVisibility = "shown" | "hidden"

export const SWITCHER_OPTIONS: ReadonlyArray<{
	value: PortalSwitcherVisibility
	label: string
	Icon: typeof ShownIcon
}> = [
	{ value: "shown", label: "Show the switcher", Icon: ShownIcon },
	{ value: "hidden", label: "Hide the switcher", Icon: HiddenIcon },
]

export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/

// Mirrors the server mutator's list; both reject slugs that collide with the
// portal's own top-level section routes.
export const RESERVED_SLUGS = new Set([
	"changelog",
	"changelogs",
	"roadmap",
	"docs",
	"requests",
	"api",
	"_health",
	"_next",
	"static",
	"main",
	"new",
])

export const SLUG_ERROR =
	"Lowercase letters, numbers or hyphens (no leading or trailing hyphen, max 40 chars)."
export const RESERVED_SLUG_ERROR = "That slug is reserved by Productlane."

export const LOGO_MAX_SIZE_MB = 5
export const LOGO_MAX_SIZE_BYTES = LOGO_MAX_SIZE_MB * 1024 * 1024
export const LOGO_ACCEPT_TYPES = [
	"image/png",
	"image/jpeg",
	"image/jpg",
	"image/webp",
]
export const LOGO_ACCEPT_STRING = LOGO_ACCEPT_TYPES.join(",")

export function resolveTeamId(value: string): string | null {
	return value === WORKSPACE_DEFAULT_TEAM || value === "" ? null : value
}

export function teamIdToSelectValue(teamId: string | null | undefined): string {
	return teamId ?? WORKSPACE_DEFAULT_TEAM
}

// A disabled or removed address sends from the workspace primary, so the picker shows that.
export function addressIdToSelectValue(
	addressId: string | null | undefined,
	enabledIds: readonly string[],
): string {
	return addressId && enabledIds.includes(addressId)
		? addressId
		: WORKSPACE_DEFAULT_TEAM
}

export function resolveAddressId(value: string): string | null {
	return value === WORKSPACE_DEFAULT_TEAM || value === "" ? null : value
}

export function visibilityToShowInSelector(
	value: PortalSwitcherVisibility,
): boolean {
	return value === "shown"
}

export function showInSelectorToVisibility(
	showInSelector: boolean,
): PortalSwitcherVisibility {
	return showInSelector ? "shown" : "hidden"
}

export function slugify(input: string): string {
	return input
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9-]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 40)
}

export function validateSlug(slug: string): string | null {
	if (!SLUG_PATTERN.test(slug)) return SLUG_ERROR
	if (RESERVED_SLUGS.has(slug)) return RESERVED_SLUG_ERROR
	return null
}

export function portalHostname(workspace: {
	customDomain?: string | null
	domain?: string | null
}): string {
	return workspace.customDomain
		? workspace.customDomain
		: `${workspace.domain ?? ""}.productlane.com`
}

/** The public URL of a portal; `null` is Main, which moves to /main once a named portal is the default. */
export function portalPublicUrl(
	workspace: { customDomain?: string | null; domain?: string | null },
	instance: { slug: string; customDomain?: string | null } | null,
	hasNamedDefault: boolean,
): string {
	const hostname = portalHostname(workspace)
	if (!instance) {
		return hasNamedDefault ? `https://${hostname}/main` : `https://${hostname}`
	}
	return instance.customDomain
		? `https://${instance.customDomain}`
		: `https://${hostname}/${instance.slug}`
}

export type PortalOverrides = Required<PortalInstanceOverrideArgs>

// Mirrors the client mutator's defaults, so an untouched section creates the
// same row as omitting the fields.
export const DEFAULT_PORTAL_OVERRIDES: PortalOverrides = {
	overridesAuth: false,
	isGoogleAuthRequired: false,
	googleDomain: null,
	overridesCustomLinks: false,
	usePortalLogoUrl: false,
	portalLogoUrl: null,
	changelogLink1Description: null,
	changelogLink1Url: null,
	changelogLink2Description: null,
	changelogLink2Url: null,
	overridesTheme: false,
	darkMode: null,
	accentColor: null,
	accentTextColor: null,
	buttonTextColor: null,
	darkModeAccentColor: null,
	darkModeAccentTextColor: null,
	darkModeButtonTextColor: null,
	backgroundColor: null,
	darkModeBackgroundColor: null,
	portalEmbedImageUrl: null,
}

type ThemeInstance = Pick<
	PortalInstanceOverrideArgs,
	| "accentColor"
	| "accentTextColor"
	| "buttonTextColor"
	| "darkModeAccentColor"
	| "darkModeAccentTextColor"
	| "darkModeButtonTextColor"
>

/**
 * What turning a portal's own theme on writes. A portal that never had colors
 * starts from the workspace's, dark mode included, so nothing on it changes.
 */
export function themeOverrideArgs(
	instance: ThemeInstance,
	workspace: {
		darkMode?: boolean | null
		accentColor: string
		buttonTextColor: string
		darkModeAccentColor: string
		darkModeButtonTextColor: string
	},
): PortalInstanceOverrideArgs {
	const hasOwnColors = [
		instance.accentColor,
		instance.accentTextColor,
		instance.buttonTextColor,
		instance.darkModeAccentColor,
		instance.darkModeAccentTextColor,
		instance.darkModeButtonTextColor,
	].some((value) => value != null)
	if (hasOwnColors) return { overridesTheme: true }
	return {
		overridesTheme: true,
		darkMode: workspace.darkMode ?? null,
		accentColor: workspace.accentColor,
		buttonTextColor: workspace.buttonTextColor,
		darkModeAccentColor: workspace.darkModeAccentColor,
		darkModeButtonTextColor: workspace.darkModeButtonTextColor,
	}
}
